import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Clock, Coins, Globe2, Pencil } from 'lucide-react';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Select } from '@/components/Select';
import { Company, LocalizationOptions, companiesService } from '@/services/companies.service';

export interface LocationValue { country: string; defaultCurrency: string; timezone: string }

// The markets most clients are in, listed first.
const POPULAR = ['AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'PK', 'EG'];

let cached: LocalizationOptions | null = null;
export function useLocalizationOptions() {
  const [opts, setOpts] = useState<LocalizationOptions | null>(cached);
  useEffect(() => {
    if (cached) return;
    companiesService.localizationOptions().then((o) => { cached = o; setOpts(o); }).catch(() => { /* interceptor toasts */ });
  }, []);
  return opts;
}

/**
 * Country, currency and timezone for a company. The company can't change
 * these in its CRM — only VAT — so this is the one place they're set. Blank
 * currency/timezone means "the country's default".
 */
export function LocationFields({ value, onChange, options, disabled }: {
  value: LocationValue;
  onChange: (v: LocationValue) => void;
  options: LocalizationOptions | null;
  disabled?: boolean;
}) {
  const country = options?.countries.find((c) => c.code === value.country);
  const [popular, rest] = useMemo(() => {
    const list = options?.countries ?? [];
    return [POPULAR.map((c) => list.find((x) => x.code === c)).filter(Boolean) as typeof list, list.filter((c) => !POPULAR.includes(c.code))];
  }, [options]);

  return (
    <div className="grid gap-x-4 sm:grid-cols-3">
      <FormField label="Country" hint="sets the defaults">
        <Select value={value.country} disabled={disabled || !options}
          onChange={(e) => onChange({ country: e.target.value, defaultCurrency: '', timezone: '' })}>
          <option value="">{options ? 'Not set' : 'Loading…'}</option>
          {popular.length > 0 && <optgroup label="Most used">{popular.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</optgroup>}
          <optgroup label="All countries">{rest.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</optgroup>
        </Select>
      </FormField>
      <FormField label="Currency" hint="deals, documents, prices">
        <Select value={value.defaultCurrency} disabled={disabled || !options} onChange={(e) => onChange({ ...value, defaultCurrency: e.target.value })}>
          <option value="">{country ? `Country default — ${country.currency}` : 'Country default (USD)'}</option>
          {(options?.currencies ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </FormField>
      <FormField label="Timezone">
        <Select value={value.timezone} disabled={disabled || !options} onChange={(e) => onChange({ ...value, timezone: e.target.value })}>
          <option value="">{country ? `Country default — ${country.timezone.replace(/_/g, ' ')}` : 'Country default (UTC)'}</option>
          {(options?.timezones ?? []).map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </Select>
      </FormField>
    </div>
  );
}

/** What the CRM will actually use for this company. */
export function resolvedLocation(v: LocationValue, options: LocalizationOptions | null) {
  const c = options?.countries.find((x) => x.code === v.country);
  return {
    countryLabel: c?.label ?? (v.country || 'Not set'),
    currency: v.defaultCurrency || c?.currency || 'USD',
    timezone: v.timezone || c?.timezone || 'UTC',
    vat: c ? `${c.vatRatePct}%${c.vatInclusive ? ' incl.' : ''}` : '—',
  };
}

/** Company → Details: location & currency, editable here only. */
export function CompanyLocationCard({ company, onChanged }: { company: Company; onChanged: () => void }) {
  const options = useLocalizationOptions();
  const saved: LocationValue = { country: company.country ?? '', defaultCurrency: company.defaultCurrency ?? '', timezone: company.timezone ?? '' };
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<LocationValue>(saved);
  const [busy, setBusy] = useState(false);
  const r = resolvedLocation(editing ? value : saved, options);

  const save = async () => {
    setBusy(true);
    try {
      await companiesService.update(company.companyUid, value);
      toast.success(`${company.name} now uses ${resolvedLocation(value, options).currency}`);
      setEditing(false);
      onChanged();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border bg-surface shadow-soft">
      <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">Location & currency</h3>
          <p className="mt-0.5 text-xs text-text-muted">The client sees these read-only in the CRM; they can still change VAT.</p>
        </div>
        {!editing && (
          <Button size="sm" variant="secondary" iconLeft={<Pencil size={13} />} onClick={() => { setValue(saved); setEditing(true); }}>Change</Button>
        )}
      </header>
      <div className="p-5">
        {editing ? (
          <>
            <LocationFields value={value} onChange={setValue} options={options} disabled={busy} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
              <Button size="sm" loading={busy} onClick={save}>Save</Button>
            </div>
            <p className="mt-2 text-2xs text-text-muted">Existing deals and documents keep their currency — only new ones change.</p>
          </>
        ) : (
          <dl className="grid gap-3 sm:grid-cols-3">
            {[
              { icon: Globe2, label: 'Country', value: r.countryLabel, sub: `VAT ${r.vat}` },
              { icon: Coins, label: 'Currency', value: r.currency, sub: company.defaultCurrency ? 'Set here' : 'Country default' },
              { icon: Clock, label: 'Timezone', value: r.timezone.replace(/_/g, ' '), sub: company.timezone ? 'Set here' : 'Country default' },
            ].map((x) => (
              <div key={x.label} className="flex items-center gap-3 rounded-xl border border-border bg-bg/60 px-3 py-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><x.icon size={15} /></span>
                <div className="min-w-0">
                  <dt className="text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted">{x.label}</dt>
                  <dd className="truncate text-sm font-semibold text-text-primary">{x.value}</dd>
                  <dd className="text-2xs text-text-muted">{x.sub}</dd>
                </div>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}
